import { Extras } from '@sentry/types';

import { Cookies, Debug } from '@hh.ru/browser-api-utils';
import { initBrowserClient } from '@hh.ru/sentry-js';
import { Config } from '@hh.ru/sentry-js/lib/types';

import { kebabToCamelCase } from '../../utils/notationConverter';

interface ErrorData extends Extras {
    sentryFingerprint?: string[];
}

type SentryInitResult = ReturnType<typeof initBrowserClient> | undefined;

let sentryInited = false;
let initedSentryClient: SentryInitResult;

export const getSentryClient = (): SentryInitResult => {
    return initedSentryClient;
};

export default (serviceName: string, params: Partial<Config> = {}): ReturnType<typeof initBrowserClient> | null => {
    const serviceNameCamelCase = kebabToCamelCase(serviceName);

    if (!sentryInited) {
        Debug.init();
        sentryInited = true;

        const serviceGlobalVars = window.globalServiceVars[serviceNameCamelCase];

        const sentryClient = initBrowserClient({
            sentryLoggingEnabled: serviceGlobalVars.features.sentry_logging,
            sentryJsConfig: serviceGlobalVars.features.sentry_js_config,
            sentryDSN: serviceGlobalVars.sentryDSN,
            buildVersion: `${serviceName}@${serviceGlobalVars.build}`,
            staticHost: serviceGlobalVars.staticHost,
            publicBuildPath: `${serviceGlobalVars.staticHost}/`,
            getUser: () => ({
                id: String(Cookies.get('hhuid')),
                userType: serviceGlobalVars.userType,
            }),
            getExtraData: () => ({
                requestId: serviceGlobalVars.requestId,
                pageName: serviceGlobalVars.pageName,
            }),
            beforeSendCallback: () => {
                if (serviceGlobalVars.blockNextErrorSend) {
                    serviceGlobalVars.blockNextErrorSend = false;
                    return false;
                }

                return true;
            },
            ...params,
        });

        const handler = function (error: Error, errorData: ErrorData = {}) {
            if (!serviceGlobalVars.features.sentry_logging) {
                return;
            }

            const { sentryFingerprint, ...extra } = errorData;
            const { scope } = sentryClient;

            if (scope) {
                scope.setExtra('fromDebug', 'true');
                if (sentryFingerprint) {
                    scope.setFingerprint(sentryFingerprint);
                }
                if (extra) {
                    scope.setExtras(extra);
                }
                scope.captureException(error);
            } else {
                throw new Error(`Debug out: no Sentry scope for service ${serviceNameCamelCase}`);
            }
        };
        Debug.registerLog('out', Debug.viewMods.PRODUCTION, handler);
        initedSentryClient = sentryClient;
        return sentryClient;
    }
    return null;
};
